import { CONSTRAINT_LEVELS, REPORT_KIND_LABELS } from './constants';
import { StepId } from './steps';
import { AdvancedOptions, AgeRange, ReportRequestState } from './types';
import { describeInstance } from '../params/instanceSize';

export interface SummaryItem {
  label: string;
  value: string;
}

export interface SummarySection {
  id: StepId;
  title: string;
  items: SummaryItem[];
}

const pct = (fraction: number) => `${Math.round(fraction * 100)}%`;

export const formatAgeRanges = (ranges: AgeRange[]) =>
  ranges.map((r) => `${r.min}–${r.max} (${pct(r.fraction)})`).join(', ');

const list = (values: string[], empty = 'None') =>
  values.length ? values.join(', ') : empty;

const count = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

const ADVANCED_LABELS: Partial<Record<keyof AdvancedOptions, string>> = {
  include_texture: 'Texture analysis',
  texture_data_only: 'Texture data only',
  texture_only: 'Texture only report',
  bootstrap_pq: 'Bootstrap PQ',
  use_hyperlocal_glp: 'Hyperlocal GLP',
  combine_artificial_sweeteners: 'Combine artificial sweeteners',
  wipe_out_imputed_ggvars: 'Wipe out imputed GGVars',
  only_include_parents: 'Only parents',
  include_archetypes: 'Archetypes',
  generate_ppt: 'PowerPoint',
  profilingEnabled: 'Profiling',
  includeNoveltyScores: 'Novelty scores',
};

const UPDATE_STRATEGY_LABELS = {
  ensure_exact_pq: 'Ensure exact PQ',
  version_controled_translation: 'Version controlled translation',
  standard: 'Standard',
};

const enabledAdvanced = (
  state: ReportRequestState,
  keys: (keyof AdvancedOptions)[],
) => keys.filter((k) => state.advanced[k]).map((k) => ADVANCED_LABELS[k] ?? k);

const productNames = (state: ReportRequestState) =>
  Array.from(new Set(state.products.map((p) => p.name)));

const inheritItems = (state: ReportRequestState): SummaryItem[] => {
  const inherit = state.reportIdToInherit.trim();
  return inherit
    ? [{ label: 'Inherit data sources from', value: inherit }]
    : [];
};

/** Compute choices, always shown so nobody is surprised by the box a report runs on. */
const computeItems = (state: ReportRequestState): SummaryItem[] => [
  { label: 'Instance size', value: describeInstance(state) },
  ...(state.jobDefinitionOverride
    ? [{ label: 'Job definition', value: state.jobDefinitionOverride }]
    : []),
];

const updateSections = (state: ReportRequestState): SummarySection[] => {
  const source = state.updateSource;
  const original = source?.originalProducts ?? [];
  const originalWorkspaces = source?.originalWorkspaces ?? [];
  const added = productNames(state).filter((n) => !original.includes(n));
  const enabled = enabledAdvanced(state, [
    'wipe_out_imputed_ggvars',
    'profilingEnabled',
  ]);
  if (state.advanced.plotNorm) enabled.push('Plot norms');
  return [
    {
      id: 'source',
      title: 'Source report',
      items: [
        { label: 'Project', value: state.projectName },
        { label: 'Client', value: state.clientName },
        { label: 'Target group', value: source?.targetGroupName ?? '—' },
        { label: 'Report ID', value: source?.reportId ?? '—' },
      ],
    },
    {
      id: 'competitiveSet',
      title: 'Competitive set',
      items: [
        { label: 'Existing products', value: String(original.length) },
        { label: 'New products', value: list(added) },
        {
          label: 'New workspaces',
          value: list(
            state.allowedWorkspaces.filter(
              (w) => !originalWorkspaces.includes(w),
            ),
          ),
        },
        { label: 'Categories', value: list(state.categories) },
      ],
    },
    {
      id: 'options',
      title: 'Options',
      items: [
        {
          label: 'Update strategy',
          value: UPDATE_STRATEGY_LABELS[state.updateStrategy],
        },
        { label: 'Enabled', value: list(enabled) },
        ...inheritItems(state),
        ...computeItems(state),
      ],
    },
  ];
};

const MARKET_SURVEY_OPTIONS: (keyof AdvancedOptions)[] = [
  'include_texture',
  'texture_data_only',
  'texture_only',
  'bootstrap_pq',
  'use_hyperlocal_glp',
  'combine_artificial_sweeteners',
  'wipe_out_imputed_ggvars',
  'only_include_parents',
  'include_archetypes',
  'generate_ppt',
  'profilingEnabled',
  'includeNoveltyScores',
];

const demographicItems = (state: ReportRequestState): SummaryItem[] => {
  const ethnicities = Object.entries(state.ethnicities)
    .filter(([, on]) => on)
    .map(([k]) => k);
  const items: SummaryItem[] = [
    { label: 'Country', value: state.country?.toUpperCase() ?? '—' },
  ];
  if (state.clts.length)
    items.push({ label: 'Regions', value: list(state.clts) });
  items.push(
    {
      label: 'Gender',
      value: `${pct(state.includeFemale)} female / ${pct(1 - state.includeFemale)} male`,
    },
    { label: 'Ages', value: formatAgeRanges(state.ageRanges) },
    { label: 'Smoking', value: state.smokingHabits },
    { label: 'Sans Rejectors', value: state.sansRejector ? 'Yes' : 'No' },
  );
  if (ethnicities.length)
    items.push({ label: 'Ethnicities', value: list(ethnicities) });
  return items;
};

const marketSurveySections = (state: ReportRequestState): SummarySection[] => {
  const classDb = state.customClassDbName.trim();
  return [
    {
      id: 'project',
      title: 'Project',
      items: [
        { label: 'Project', value: state.projectName },
        { label: 'Client', value: state.clientName },
        { label: 'Workspace', value: state.workspace?.name ?? '—' },
      ],
    },
    {
      id: 'demographics',
      title: 'Demographics',
      items: demographicItems(state),
    },
    {
      id: 'competitiveSet',
      title: 'Competitive set',
      items: [
        {
          label: 'Products',
          value: count(productNames(state).length, 'product'),
        },
        { label: 'Workspaces', value: list(state.allowedWorkspaces) },
        { label: 'Categories', value: list(state.categories) },
        ...(classDb
          ? [
              {
                label: 'Custom class DB',
                value: `${classDb} (${count(state.customClassDbProducts.length, 'product')})`,
              },
            ]
          : []),
      ],
    },
    {
      id: 'options',
      title: 'Options',
      items: [
        {
          label: 'Enabled',
          value: list(enabledAdvanced(state, MARKET_SURVEY_OPTIONS)),
        },
        ...inheritItems(state),
        ...computeItems(state),
      ],
    },
  ];
};

const parentSection = (state: ReportRequestState): SummarySection => ({
  id: 'parent',
  title: 'Parent report',
  items: [
    { label: 'Project', value: state.parent?.projectName ?? '—' },
    { label: 'Client', value: state.parent?.clientName ?? '—' },
    { label: 'Target group', value: state.parent?.targetGroupName ?? '—' },
  ],
});

const newReferenceFlavorsLabel = (values: string[]) => {
  if (values.includes('TRUE') && values.includes('FALSE')) return 'Both';
  return values.includes('TRUE') ? 'Allowed' : 'Not allowed';
};

const truthy = (values: (string | false)[]) =>
  values.filter((v): v is string => !!v);

const optimizationSections = (state: ReportRequestState): SummarySection[] => {
  const opt = state.optimization;
  const levels = CONSTRAINT_LEVELS.filter((c) =>
    opt.constraintLevels.includes(c.level),
  ).map((c) => c.label);
  const pixels = opt.pixelList.trim();
  return [
    parentSection(state),
    {
      id: 'optimization',
      title: 'Optimization',
      items: [
        { label: 'Product', value: opt.targetProduct ?? '—' },
        {
          label: 'Optimizes',
          value: list(
            truthy([
              opt.optimizeFlavor && 'Flavor',
              opt.optimizeTexture && 'Texture',
            ]),
          ),
        },
        { label: 'Constraint levels', value: list(levels) },
        {
          label: 'New reference flavors',
          value: newReferenceFlavorsLabel(opt.newReferenceFlavors),
        },
        {
          label: 'GGVar constraints',
          value: String(opt.ggvarConstraints.length),
        },
        { label: 'Locked flavors', value: list(opt.refFlavorsToLock) },
        { label: 'Removed flavors', value: list(opt.refFlavorsToRemove) },
        {
          label: 'Tuned flavors',
          value: list(
            opt.refFlavorsToTune.map((t) => `${t.flavor} (${t.min}–${t.max}%)`),
          ),
        },
      ],
    },
    {
      id: 'options',
      title: 'Options',
      items: [
        {
          label: 'Enabled',
          value: list(
            truthy([
              opt.showNorms && 'Show norms',
              opt.tuningPlots && 'Tuning plots',
              opt.plotNorm && 'Plot norms',
            ]),
          ),
        },
        ...(pixels ? [{ label: 'Pixels', value: pixels }] : []),
        ...inheritItems(state),
        ...computeItems(state),
      ],
    },
  ];
};

const mixAndMatchSections = (state: ReportRequestState): SummarySection[] => {
  return [
    parentSection(state),
    {
      id: 'mixAndMatch',
      title: 'Versions',
      items: [
        { label: 'Product', value: state.optimization.targetProduct ?? '—' },
        ...state.mixAndMatch.map((m) => ({
          label: m.label,
          value: `Versions ${list(m.versions)}`,
        })),
      ],
    },
    {
      id: 'options',
      title: 'Options',
      items: [...inheritItems(state), ...computeItems(state)],
    },
  ];
};

/** A plain-text recap of the request, one section per step. */
export const summarise = (state: ReportRequestState): SummarySection[] => {
  if (state.mode === 'update') return updateSections(state);
  if (!state.reportKind || state.reportKind === 'update_products') return [];
  const type: SummarySection = {
    id: 'type',
    title: 'Report type',
    items: [{ label: 'Type', value: REPORT_KIND_LABELS[state.reportKind] }],
  };
  const byKind = {
    market_survey: marketSurveySections,
    optimization: optimizationSections,
    mix_and_match: mixAndMatchSections,
  };
  return [type, ...byKind[state.reportKind](state)];
};
